import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import * as XLSX from "xlsx";
import "./BillsFilter.css";

function BillsFilter() {
  const [bills, setBills] = useState([]);
  const [productNames, setProductNames] = useState([]);
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    productType: "",
    paymentMethod: "",
    discount: "",
  });
  const [loading, setLoading] = useState(false);
  
  const currentUser = useSelector((state) => state.AuthReducer?.user);
  const dispatch = useDispatch();

  const extractLocId = (loc) => {
    if (!loc) return null;
    
    if (typeof loc === "string") return loc;
    
    if (loc.$oid) return loc.$oid;
    
    if (loc._id) {
      if (typeof loc._id === 'string') return loc._id;
      if (loc._id.$oid) return loc._id.$oid;
      return loc._id.toString();
    }
    
    return loc.toString ? loc.toString() : String(loc);
  };

  useEffect(() => {
    fetchBills();
  }, []);

  const fetchBills = async () => {
    setLoading(true);
    try {
      let response;
      
      if (currentUser?.role === "admin") {
        response = await axios.get("/api/Bills/getAllBills");
        console.log("📋 Admin - Chargement de TOUTES les factures");
      } 
      else if (currentUser?.localisation) {
        const userLocationId = extractLocId(currentUser.localisation);
        console.log("📍 Filtrage par localisation ID:", userLocationId);
        
        const allResponse = await axios.get("/api/Bills/getAllBills");
        const allBills = allResponse.data;
        
        const filteredBills = allBills.filter(bill => {
          const billLocationId = extractLocId(bill.localisation);
          return billLocationId === userLocationId;
        });
        
        response = { data: filteredBills };
        console.log(`✅ Factures filtrées: ${filteredBills.length} sur ${allBills.length}`);
      } 
      else {
        response = await axios.get("/api/Bills/getAllBills");
        console.log("⚠️ Aucune localisation définie - Chargement de toutes les factures");
      }
      
      setBills(response.data || []);
      extractProductNames(response.data || []);
      
    } catch (error) {
      console.error("Erreur lors du chargement des factures:", error);
      setBills([]);
    } finally {
      setLoading(false);
    }
  };

  const extractProductNames = (billsData) => {
    const namesSet = new Set();
    if (billsData && billsData.length > 0) {
      billsData.forEach((bill) => {
        if (bill.items && Array.isArray(bill.items)) {
          bill.items.forEach((item) => {
            if (item.name) {
              namesSet.add(item.name);
            }
          });
        }
      });
    }
    setProductNames([...namesSet]);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const normalizeDate = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
  };

const calculateBillTotalTTC = (bill) => {
  // Vérifier si 'total' existe et est un nombre
  if (bill.total !== undefined && bill.total !== null && typeof bill.total === 'number') {
    // Si 'total' existe, supposons qu'il s'agit déjà du montant TTC
    return bill.total;
  } 
  // Si nous avons HT et TVA séparément, les additionner
  else if (bill.totalHT !== undefined && bill.tva !== undefined) {
    const ht = Number(bill.totalHT) || 0;
    const tva = Number(bill.tva) || 0;
    return ht + tva;
  } 
  // Si un champ totalTTC explicite existe
  else if (bill.totalTTC !== undefined) {
    return bill.totalTTC;
  } 
  // Fallback
  else {
    return 0;
  }
};

  // Utilisation de useMemo pour éviter des recalculs inutiles
  const filteredBills = useMemo(() => {
    let filtered = [...bills];

    if (filters.startDate) {
      filtered = filtered.filter(
        (bill) => normalizeDate(bill.date) >= normalizeDate(filters.startDate)
      );
    }

    if (filters.endDate) {
      filtered = filtered.filter(
        (bill) => normalizeDate(bill.date) <= normalizeDate(filters.endDate)
      );
    }

    if (filters.productType) {
      filtered = filtered.filter(
        (bill) =>
          bill.items && bill.items.some((item) => item.name === filters.productType)
      );
    }

    if (filters.paymentMethod) {
      filtered = filtered.filter(
        (bill) => bill.paymentMethod === filters.paymentMethod
      );
    }

    if (filters.discount) {
      filtered = filtered.filter(
        (bill) => bill.discount === parseFloat(filters.discount)
      );
    }

    return filtered;
  }, [bills, filters]);

  // Calculer les statistiques basées sur les factures filtrées
  const statistics = useMemo(() => {
    const totalFilteredTTC = filteredBills.reduce((acc, bill) => acc + calculateBillTotalTTC(bill), 0);
    const totalAllTTC = bills.reduce((acc, bill) => acc + calculateBillTotalTTC(bill), 0);
    
    return {
      totalFilteredCount: filteredBills.length,
      totalFilteredTTC: totalFilteredTTC,
      totalAllCount: bills.length,
      totalAllTTC: totalAllTTC,
      // Statistiques par méthode de paiement (filtrées)
      paymentStats: filteredBills.reduce((acc, bill) => {
        const method = bill.paymentMethod || 'Non défini';
        const amount = calculateBillTotalTTC(bill);
        
        if (!acc[method]) {
          acc[method] = { count: 0, total: 0 };
        }
        acc[method].count += 1;
        acc[method].total += amount;
        
        return acc;
      }, {})
    };
  }, [filteredBills, bills]);

  const exportToExcel = () => {
    if (filteredBills.length === 0) {
      alert("Aucune donnée à exporter !");
      return;
    }

    const excelData = filteredBills.map(bill => {
      const produits = bill.items && Array.isArray(bill.items) 
        ? bill.items.map(item => `${item.name} (x${item.quantity}) - ${item.price?.toFixed(2)} DT`).join('; ')
        : '';

      const dateFormatted = bill.date 
        ? new Date(bill.date).toLocaleDateString('fr-FR')
        : '';

      return {
        "Date": dateFormatted,
        "Numéro de Table": bill.tableNumber || "N/A",
        "Produits": produits,
        "Total TTC": calculateBillTotalTTC(bill).toFixed(2) + " DT",
        "Moyen de Paiement": bill.paymentMethod || "Non défini",
        "Réduction": bill.discount > 0 ? `${bill.discount}%` : "0%",
        "Localisation": bill.localisation?.name || bill.localisation || "Non définie"
      };
    });

    // Ajouter une ligne de totaux filtrés
    const totalRow = {
      "Date": "TOTAL FILTRÉ",
      "Numéro de Table": "",
      "Produits": "",
      "Total TTC": statistics.totalFilteredTTC.toFixed(2) + " DT",
      "Moyen de Paiement": "",
      "Réduction": "",
      "Localisation": `${filteredBills.length} facture(s) filtrée(s)`
    };
    
    excelData.push(totalRow);

    // Ajouter une ligne de total général (optionnel)
    const generalTotalRow = {
      "Date": "TOTAL GÉNÉRAL",
      "Numéro de Table": "",
      "Produits": "",
      "Total TTC": statistics.totalAllTTC.toFixed(2) + " DT",
      "Moyen de Paiement": "",
      "Réduction": "",
      "Localisation": `${bills.length} facture(s) au total`
    };
    
    excelData.push(generalTotalRow);

    const ws = XLSX.utils.json_to_sheet(excelData);
    
    const wscols = [
      { wch: 12 },
      { wch: 15 },
      { wch: 40 },
      { wch: 15 },
      { wch: 20 },
      { wch: 12 },
      { wch: 20 },
    ];
    ws['!cols'] = wscols;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Factures Filtrées");

    const dateStr = new Date().toISOString().split('T')[0];
    const fileName = `rapport_factures_${dateStr}.xlsx`;

    XLSX.writeFile(wb, fileName);
  };

  const exportAllToExcel = () => {
    if (bills.length === 0) {
      alert("Aucune donnée à exporter !");
      return;
    }

    const excelData = bills.map(bill => {
      const produits = bill.items && Array.isArray(bill.items) 
        ? bill.items.map(item => `${item.name} (x${item.quantity}) - ${item.price?.toFixed(2)} DT`).join('; ')
        : '';

      const dateFormatted = bill.date 
        ? new Date(bill.date).toLocaleDateString('fr-FR')
        : '';

      return {
        "Date": dateFormatted,
        "Numéro de Table": bill.tableNumber || "N/A",
        "Produits": produits,
        "Total TTC": calculateBillTotalTTC(bill).toFixed(2) + " DT",
        "Moyen de Paiement": bill.paymentMethod || "Non défini",
        "Réduction": bill.discount > 0 ? `${bill.discount}%` : "0%",
        "Localisation": bill.localisation?.name || bill.localisation || "Non définie"
      };
    });

    const totalRow = {
      "Date": "TOTAL GÉNÉRAL",
      "Numéro de Table": "",
      "Produits": "",
      "Total TTC": statistics.totalAllTTC.toFixed(2) + " DT",
      "Moyen de Paiement": "",
      "Réduction": "",
      "Localisation": `${bills.length} facture(s) au total`
    };
    
    excelData.push(totalRow);

    const ws = XLSX.utils.json_to_sheet(excelData);
    
    const wscols = [
      { wch: 12 },
      { wch: 15 },
      { wch: 40 },
      { wch: 15 },
      { wch: 20 },
      { wch: 12 },
      { wch: 20 },
    ];
    ws['!cols'] = wscols;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Toutes les Factures");

    const dateStr = new Date().toISOString().split('T')[0];
    const fileName = `rapport_complet_${dateStr}.xlsx`;

    XLSX.writeFile(wb, fileName);
  };

  const renderLocationInfo = () => {
    if (!currentUser) return null;
    
    let locationText = "";
    if (currentUser.role === "admin") {
      locationText = "📋 VUE ADMIN - TOUTES LES LOCALISATIONS";
    } else if (currentUser.localisation) {
      const locName = currentUser.localisation.name || currentUser.localisation;
      locationText = `📍 LOCALISATION: ${locName}`;
    } else {
      locationText = "⚠️ LOCALISATION NON DÉFINIE";
    }
    
    return (
      <div className="location-info" style={{
        backgroundColor: currentUser.role === "admin" ? '#4caf50' : '#2196f3',
        color: 'white',
        padding: '10px',
        borderRadius: '5px',
        marginBottom: '15px',
        textAlign: 'center',
        fontWeight: 'bold'
      }}>
        {locationText}
        {currentUser.role !== "admin" && bills.length > 0 && (
          <div style={{ fontSize: '14px', fontWeight: 'normal', marginTop: '5px' }}>
            {bills.length} facture(s) trouvée(s) pour votre localisation
          </div>
        )}
      </div>
    );
  };

  // Fonction pour réinitialiser les filtres
  const resetFilters = () => {
    setFilters({
      startDate: "",
      endDate: "",
      productType: "",
      paymentMethod: "",
      discount: "",
    });
  };

  return (
    <div className="invoices-container">
      <h1>Gestion des Factures</h1>
      
      {renderLocationInfo()}

      <div className="filters-section">
        <div className="filter-group">
          <label htmlFor="startDate">Date de début</label>
          <input
            type="date"
            id="startDate"
            name="startDate"
            value={filters.startDate}
            onChange={handleFilterChange}
          />
        </div>
        <div className="filter-group">
          <label htmlFor="endDate">Date de fin</label>
          <input
            type="date"
            id="endDate"
            name="endDate"
            value={filters.endDate}
            onChange={handleFilterChange}
          />
        </div>
        <div className="filter-group">
          <label htmlFor="productType">Type de sous-produit</label>
          <select
            id="productType"
            name="productType"
            value={filters.productType}
            onChange={handleFilterChange}
          >
            <option value="">Tous les produits</option>
            {productNames.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label htmlFor="paymentMethod">Moyen de paiement</label>
          <select
            id="paymentMethod"
            name="paymentMethod"
            value={filters.paymentMethod}
            onChange={handleFilterChange}
          >
            <option value="">Choisir</option>
            <option value="Espece">Espèce</option>
            <option value="TPE">TPE</option>
            <option value="T.Resto">Ticket Resto</option>
            <option value="Glovo">Glovo</option>
            <option value="Yassir">Yassir</option>
            <option value="Combiné">Paiement Combiné</option>
          </select>
        </div>
        <div className="filter-group">
          <label htmlFor="discount">Réduction (%)</label>
          <input
            type="number"
            id="discount"
            name="discount"
            value={filters.discount}
            onChange={handleFilterChange}
            min="0"
            max="100"
            step="1"
          />
        </div>
        
        <div className="filter-group">
          <label>&nbsp;</label>
          <button 
            onClick={fetchBills}
            className="refresh-button"
            style={{
              backgroundColor: '#4caf50',
              color: 'white',
              border: 'none',
              padding: '10px 15px',
              borderRadius: '5px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            🔄 Rafraîchir
          </button>
        </div>
        
        <div className="filter-group">
          <label>&nbsp;</label>
          <button 
            onClick={resetFilters}
            style={{
              backgroundColor: '#ff9800',
              color: 'white',
              border: 'none',
              padding: '10px 15px',
              borderRadius: '5px',
              cursor: 'pointer',
              fontSize: '14px',
              marginRight: '10px'
            }}
          >
            🗑️ Réinitialiser filtres
          </button>
        </div>
        
        <div className="filter-group">
          <label>&nbsp;</label>
          <button 
            onClick={exportToExcel}
            style={{
              backgroundColor: '#2196f3',
              color: 'white',
              border: 'none',
              padding: '10px 15px',
              borderRadius: '5px',
              cursor: 'pointer',
              fontSize: '14px',
              marginRight: '10px'
            }}
          >
            📊 Exporter les filtres
          </button>
        </div>
        
        {currentUser?.role === "admin" && (
          <div className="filter-group">
            <label>&nbsp;</label>
            <button 
              onClick={exportAllToExcel}
              style={{
                backgroundColor: '#9c27b0',
                color: 'white',
                border: 'none',
                padding: '10px 15px',
                borderRadius: '5px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              📈 Exporter tout
            </button>
          </div>
        )}
      </div>

      {/* Statistiques avec indicateurs visuels */}
      <div className="statistics" style={{
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '10px'
      }}>
        {/* Carte 1: Total des factures chargées */}
        <div className="stat-card" style={{
          backgroundColor: '#e3f2fd',
          padding: '15px',
          borderRadius: '8px',
          flex: '1',
          minWidth: '200px',
          border: '2px solid #1976d2'
        }}>
          <h3 style={{ margin: '0 0 10px 0', color: '#1976d2' }}>
            Factures Chargées
          </h3>
          <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#1976d2' }}>
            {statistics.totalAllCount}
          </p>
          <p style={{ fontSize: '14px', color: '#1976d2', margin: '5px 0 0 0' }}>
            Total général
          </p>
        </div>
        
        {/* Carte 2: Total TTC des factures chargées */}
        <div className="stat-card" style={{
          backgroundColor: '#e8f5e9',
          padding: '15px',
          borderRadius: '8px',
          flex: '1',
          minWidth: '200px',
          border: '2px solid #388e3c'
        }}>
          <h3 style={{ margin: '0 0 10px 0', color: '#388e3c' }}>
            Total TTC (Toutes)
          </h3>
          <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#388e3c' }}>
            {statistics.totalAllTTC.toFixed(2)} DT
          </p>
          <p style={{ fontSize: '14px', color: '#388e3c', margin: '5px 0 0 0' }}>
            Montant général
          </p>
        </div>
        
        {/* Carte 3: Factures filtrées */}
        <div className="stat-card" style={{
          backgroundColor: '#fff3e0',
          padding: '15px',
          borderRadius: '8px',
          flex: '1',
          minWidth: '200px',
          border: filteredBills.length > 0 ? '2px solid #f57c00' : '2px solid #ccc'
        }}>
          <h3 style={{ 
            margin: '0 0 10px 0', 
            color: filteredBills.length > 0 ? '#f57c00' : '#999' 
          }}>
            Factures Filtrées
            {filteredBills.length < statistics.totalAllCount && filteredBills.length > 0 && (
              <span style={{ fontSize: '12px', marginLeft: '10px', color: '#666' }}>
                ({Math.round((filteredBills.length / statistics.totalAllCount) * 100)}%)
              </span>
            )}
          </h3>
          <p style={{ 
            fontSize: '24px', 
            fontWeight: 'bold', 
            color: filteredBills.length > 0 ? '#f57c00' : '#999' 
          }}>
            {statistics.totalFilteredCount}
          </p>
          <p style={{ fontSize: '14px', color: '#f57c00', margin: '5px 0 0 0' }}>
            {filteredBills.length > 0 ? 'Correspondent aux filtres' : 'Aucune correspondance'}
          </p>
        </div>
        
        {/* Carte 4: Total TTC filtré */}
        <div className="stat-card" style={{
          backgroundColor: '#fce4ec',
          padding: '15px',
          borderRadius: '8px',
          flex: '1',
          minWidth: '200px',
          border: filteredBills.length > 0 ? '2px solid #c2185b' : '2px solid #ccc'
        }}>
          <h3 style={{ 
            margin: '0 0 10px 0', 
            color: filteredBills.length > 0 ? '#c2185b' : '#999' 
          }}>
            Total TTC (Filtré)
          </h3>
          <p style={{ 
            fontSize: '24px', 
            fontWeight: 'bold', 
            color: filteredBills.length > 0 ? '#c2185b' : '#999' 
          }}>
            {statistics.totalFilteredTTC.toFixed(2)} DT
          </p>
          <p style={{ fontSize: '14px', color: '#c2185b', margin: '5px 0 0 0' }}>
            {filteredBills.length > 0 
              ? statistics.totalAllTTC > 0 
                ? `${Math.round((statistics.totalFilteredTTC / statistics.totalAllTTC) * 100)}% du total`
                : 'Montant filtré'
              : 'Aucun montant'
            }
          </p>
        </div>
      </div>

      {/* Indicateur de filtrage actif */}
      {Object.values(filters).some(value => value !== "") && (
        <div style={{
          backgroundColor: '#fff8e1',
          padding: '10px',
          borderRadius: '5px',
          marginBottom: '20px',
          borderLeft: '4px solid #ffb300'
        }}>
          <p style={{ margin: 0, color: '#5d4037', fontWeight: 'bold' }}>
            🔍 Filtres actifs: 
            {filters.startDate && ` Depuis le ${filters.startDate}`}
            {filters.endDate && ` Jusqu'au ${filters.endDate}`}
            {filters.productType && ` | Produit: ${filters.productType}`}
            {filters.paymentMethod && ` | Paiement: ${filters.paymentMethod}`}
            {filters.discount && ` | Réduction: ${filters.discount}%`}
          </p>
          <p style={{ margin: '5px 0 0 0', fontSize: '14px', color: '#795548' }}>
            Affichage de {filteredBills.length} facture(s) sur {statistics.totalAllCount}
          </p>
        </div>
      )}

      <table className="invoices-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Numéro de Table</th>
            <th>Produits</th>
            <th>Total TTC</th>
            <th>Moyen de Paiement</th>
            <th>Réduction</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan="6" className="alert loading">
                Chargement...
              </td>
            </tr>
          ) : filteredBills.length === 0 ? (
            <tr>
              <td colSpan="6" className="alert">
                {bills.length === 0 
                  ? "Aucune facture chargée" 
                  : "Aucune facture ne correspond aux critères de filtrage"
                }
              </td>
            </tr>
          ) : (
            filteredBills.map((bill) => (
              <tr key={bill._id}>
                <td>{new Date(bill.date).toLocaleDateString()}</td>
                <td>{bill.tableNumber || "N/A"}</td>
                <td>
                  <ul style={{ margin: 0, paddingLeft: '20px', maxHeight: '100px', overflowY: 'auto' }}>
                    {bill.items && bill.items.map((item, index) => (
                      <li key={index} style={{ fontSize: '12px' }}>
                        {item.name} (x{item.quantity}) - {item.price?.toFixed(2)} DT
                      </li>
                    ))}
                  </ul>
                </td>
                <td style={{ fontWeight: 'bold' }}>{calculateBillTotalTTC(bill).toFixed(2)} DT</td>
                <td>
                  <span style={{
                    backgroundColor: bill.paymentMethod === 'Espece' ? '#4caf50' :
                                   bill.paymentMethod === 'TPE' ? '#2196f3' :
                                   bill.paymentMethod === 'T.Resto' ? '#ff9800' :
                                   bill.paymentMethod === 'Glovo' ? '#9c27b0' :
                                   bill.paymentMethod === 'Yassir' ? '#5e0505' :
                                   bill.paymentMethod === 'Combiné' ? '#ff5722' : '#757575',
                    color: 'white',
                    padding: '3px 8px',
                    borderRadius: '3px',
                    fontSize: '12px'
                  }}>
                    {bill.paymentMethod || 'Non défini'}
                  </span>
                </td>
                <td>
                  {bill.discount > 0 ? (
                    <span style={{ color: '#d32f2f', fontWeight: 'bold' }}>
                      {bill.discount}%
                    </span>
                  ) : (
                    '0%'
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      
      <div className="total-section" style={{
        backgroundColor: '#f5f5f5',
        padding: '15px',
        borderRadius: '8px',
        marginTop: '20px',
        border: '2px solid #4caf50',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          <h2 style={{ margin: 0, color: '#388e3c' }}>
            Total TTC (Factures Filtrées) : {statistics.totalFilteredTTC.toFixed(2)} DT
          </h2>
          {filteredBills.length > 0 && (
            <p style={{ margin: '5px 0 0 0', color: '#666' }}>
              {filteredBills.length} facture(s) correspondant aux critères de filtre
            </p>
          )}
        </div>
        
        <div style={{ textAlign: 'right' }}>
          <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
            <strong>Total général:</strong> {statistics.totalAllTTC.toFixed(2)} DT
          </p>
          {statistics.totalAllTTC > 0 && (
            <p style={{ margin: '5px 0 0 0', color: '#666', fontSize: '14px' }}>
              <strong>Pourcentage:</strong> {Math.round((statistics.totalFilteredTTC / statistics.totalAllTTC) * 100)}%
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default BillsFilter;